import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import chartDataDownloaderWorker from "workers/charts-exports-csv-worker";
import reportsWidgetDataDownloaderWorker from "workers/reports-widget-csv-worker";
import WebWorker from "workers/WebWorker";

import ChartUtil from "utils/ChartUtil";
import getLogger from "utils/logger.util";
import { getReportPreviewEnd, getReportPreviewStart } from "utils/reportUtils";
import { getFileNameDateTime } from "utils/utils.js";
import { CHART_DOWNLOAD_DATA_DIALOG_STRINGS, ReportGenerateStrings } from "constants/constantStrings";
import { EXPORT_FILE_TYPE, LOCAL_TZ_CODE } from "constants/globalConstants";

import createNotification, { ERROR_NOTIFICATION, INFO_NOTIFICATION, SUCCESS_NOTIFICATION } from "components/Notifications";

const Logger = getLogger("Chart - DownloadHanlder");

const exportCSVFile = async (headers, excelData, downloadDurationMsg, descriptionRow) => {
  const fileName = `TagsData_${getFileNameDateTime()}.xlsx`;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet();
  worksheet.columns = headers;
  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(1).alignment = { wrapText: true };
  if (descriptionRow) {
    worksheet.addRow(descriptionRow);
    worksheet.getRow(2).font = { bold: true };
    worksheet.getRow(2).alignment = { wrapText: true };
  }
  worksheet.addRows(excelData);

  ChartUtil.getExcelFileTitle(worksheet, downloadDurationMsg);

  const buffer = await workbook.xlsx.writeBuffer();
  const excelSheetData = new Blob([buffer], { type: EXPORT_FILE_TYPE });
  saveAs(excelSheetData, fileName);
  const { downloadSuccessful, downloadSuccessfulMsg } = CHART_DOWNLOAD_DATA_DIALOG_STRINGS;
  createNotification(SUCCESS_NOTIFICATION, downloadSuccessfulMsg.replace("[fileName]", fileName), downloadSuccessful);
};

const downloadChartData = (Tags, PlotData, downloadDurationMsg) => {
  const worker = new WebWorker(chartDataDownloaderWorker);
  worker.postMessage({ Tags, PlotData });
  worker.onerror = error => Logger.log("downloadChartsData - Error in Worker:", error);
  worker.onmessage = event => {
    Logger.log("downloadChartdata... Formatted data from worker: ", event.data);
    const { excelHeaders, excelData, descriptionRow } = event.data;
    exportCSVFile(excelHeaders, excelData, downloadDurationMsg, descriptionRow);
    worker.terminate();
  };
};

const fetchAndDownloadTagsData = async (Tags, api, payload, baseline, downloadDurationMsg) => {
  try {
    const { downloadStarted, downloadStartedMsg } = CHART_DOWNLOAD_DATA_DIALOG_STRINGS;
    createNotification(INFO_NOTIFICATION, downloadStartedMsg, downloadStarted);
    const startTime = performance.now(); // For debugging only
    const resp = await api(payload, baseline, true);
    const endTime = performance.now(); // For debugging only
    const PlotData = resp.data;
    Logger.info(`Time took to get ${Tags.length} tag's data with ${payload.group} seconds interval is ${(endTime - startTime) / 1000} seconds`);
    downloadChartData(Tags, PlotData, downloadDurationMsg);
  } catch (error) {
    Logger.error("Error while downloading tags data: ", error);
    const { downloadFailed, downloadFailedMsg } = CHART_DOWNLOAD_DATA_DIALOG_STRINGS;
    createNotification(ERROR_NOTIFICATION, downloadFailedMsg, downloadFailed);
  }
};

const downloadWidgetCSVInReports = (response, descriptionRow) => {
  const worker = new WebWorker(reportsWidgetDataDownloaderWorker);
  worker.postMessage({ response });
  worker.onerror = error => Logger.log("downloadChartsData - Error in Worker:", error);
  const reportDuration = () => `${getReportPreviewStart()} - ${getReportPreviewEnd()} ${LOCAL_TZ_CODE}`;
  const heading = `${ReportGenerateStrings.downloadCSVHeader}  ${response?.Header} \n from ${reportDuration()}`;

  worker.onmessage = event => {
    Logger.log("downloadChartdata... Formatted data from worker: ", event.data);
    const { excelHeaders, excelData } = event.data;
    exportCSVFile(excelHeaders, excelData, heading, descriptionRow);
    worker.terminate();
  };
};

const FileDownloadHandler = { downloadChartData, fetchAndDownloadTagsData, downloadWidgetCSVInReports };

export default FileDownloadHandler;
